import React from 'react';
import {
    Box, Paper, Stack, TextField, MenuItem, Button, Chip, Typography, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import BuscaPaciente from '../BuscaPaciente';
import { getStatusOpcoes } from '../../config/relatorios';

/** Data de hoje em ISO, no fuso local — toISOString() voltaria um dia à noite. */
const iso = (d) => {
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

/**
 * Atalhos de período. Trabalham sobre a data do navegador porque é o calendário
 * que o usuário tem na frente.
 */
export const ATALHOS_PERIODO = [
    {
        rotulo: 'Este mês',
        calcular: () => {
            const h = new Date();
            return {
                dataInicio: iso(new Date(h.getFullYear(), h.getMonth(), 1)),
                dataFim: iso(new Date(h.getFullYear(), h.getMonth() + 1, 0)),
            };
        },
    },
    {
        rotulo: 'Mês anterior',
        calcular: () => {
            const h = new Date();
            return {
                dataInicio: iso(new Date(h.getFullYear(), h.getMonth() - 1, 1)),
                dataFim: iso(new Date(h.getFullYear(), h.getMonth(), 0)),
            };
        },
    },
    {
        rotulo: 'Últimos 30 dias',
        calcular: () => {
            const h = new Date();
            const i = new Date();
            // 29 dias atrás + hoje = 30 dias de janela.
            i.setDate(i.getDate() - 29);
            return { dataInicio: iso(i), dataFim: iso(h) };
        },
    },
];

/**
 * Painel compacto de filtros de um relatório.
 *
 * O estado mora no pai (RelatorioViewer), que é quem consulta e exporta — os
 * três precisam enxergar exatamente o mesmo recorte, senão a exportação sai de
 * um filtro e a tabela de outro.
 *
 * Erro de validação só aparece depois de interação ou de uma tentativa de
 * consulta (`mostrarErros`): abrir a página já com campo em vermelho acusa o
 * usuário de algo que ele ainda não fez.
 */
export default function PainelFiltros({
    cfg,
    valores,
    onChange,
    onAplicar,
    onLimpar,
    pacienteSelecionado = null,
    onPacienteChange,
    carregando = false,
    mostrarErros = false,
    temFiltroAplicado = false,
}) {
    const temAlgum = cfg.temDatas || cfg.temStatus || cfg.temPaciente || cfg.temDataReferencia;
    if (!temAlgum) return null;

    const faltaPaciente = !!cfg.temPaciente && !valores.idPaciente;
    // Intervalo invertido devolveria lista vazia sem explicar por quê.
    const intervaloInvertido = !!cfg.temDatas
        && !!valores.dataInicio && !!valores.dataFim
        && valores.dataInicio > valores.dataFim;

    const erroPaciente = mostrarErros && faltaPaciente;
    const erroIntervalo = mostrarErros && intervaloInvertido;

    const definir = (campo) => (e) => onChange({ ...valores, [campo]: e.target.value });

    const aplicarAtalho = (atalho) => {
        onChange({ ...valores, ...atalho.calcular() });
    };

    const atalhoAtivo = (atalho) => {
        const p = atalho.calcular();
        return valores.dataInicio === p.dataInicio && valores.dataFim === p.dataFim;
    };

    return (
        <Paper
            variant="outlined"
            component="form"
            onSubmit={(e) => { e.preventDefault(); onAplicar(); }}
            sx={{ p: 1.75, mb: 2 }}
            aria-label="Filtros do relatório"
        >
            <Stack spacing={1.5}>
                {cfg.temDatas && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                            Período:
                        </Typography>
                        {ATALHOS_PERIODO.map(a => (
                            <Chip
                                key={a.rotulo}
                                label={a.rotulo}
                                size="small"
                                clickable
                                onClick={() => aplicarAtalho(a)}
                                color={atalhoAtivo(a) ? 'primary' : 'default'}
                                variant={atalhoAtivo(a) ? 'filled' : 'outlined'}
                                aria-pressed={atalhoAtivo(a)}
                            />
                        ))}
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex', gap: 1.5, flexWrap: 'wrap',
                        alignItems: 'flex-start',
                    }}
                >
                    {cfg.temPaciente && (
                        <Box sx={{ minWidth: 240 }}>
                            <BuscaPaciente
                                label="Paciente *"
                                value={pacienteSelecionado}
                                onChange={(v) => {
                                    onPacienteChange?.(v);
                                    definir('idPaciente')({ target: { value: v?.id_paciente ?? '' } });
                                }}
                                error={erroPaciente}
                                helperText={erroPaciente ? 'Selecione um paciente para consultar' : ' '}
                            />
                        </Box>
                    )}

                    {cfg.temDataReferencia && (
                        <TextField
                            type="date" size="small"
                            label={cfg.rotuloDataReferencia || 'Data de referência'}
                            sx={{ minWidth: 190 }}
                            value={valores.dataReferencia}
                            onChange={definir('dataReferencia')}
                            InputLabelProps={{ shrink: true }}
                            helperText="Em branco usa hoje"
                        />
                    )}

                    {cfg.temDatas && (
                        <>
                            <TextField
                                type="date" size="small" label="Data início"
                                sx={{ minWidth: 165 }}
                                value={valores.dataInicio}
                                onChange={definir('dataInicio')}
                                InputLabelProps={{ shrink: true }}
                                error={erroIntervalo}
                                helperText={erroIntervalo ? 'Início depois do fim' : ' '}
                            />
                            <TextField
                                type="date" size="small" label="Data fim"
                                sx={{ minWidth: 165 }}
                                value={valores.dataFim}
                                onChange={definir('dataFim')}
                                InputLabelProps={{ shrink: true }}
                                error={erroIntervalo}
                                helperText={erroIntervalo ? 'Fim antes do início' : ' '}
                            />
                        </>
                    )}

                    {cfg.temStatus && (
                        <TextField
                            select size="small" label={cfg.rotuloStatus ?? 'Status'}
                            sx={{ minWidth: 175 }}
                            value={valores.status}
                            onChange={definir('status')}
                            helperText=" "
                        >
                            {getStatusOpcoes(cfg).map(o => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                        type="submit"
                        variant="contained" color="primary" disableElevation size="small"
                        startIcon={<SearchIcon />}
                        loading={carregando}
                    >
                        Aplicar filtros
                    </Button>
                    <Button
                        type="button"
                        variant="outlined" color="inherit" size="small"
                        startIcon={<FilterAltOffIcon />}
                        onClick={onLimpar}
                        disabled={!temFiltroAplicado}
                    >
                        Limpar filtros
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
}
